import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FiAlertCircle, FiChevronLeft, FiChevronRight, FiArrowRight, FiArrowLeft } from 'react-icons/fi';
import ProductCard from '../common/ProductCard';
import productService from '../../services/productService';
import categoryService, { buildCategoryHierarchy } from '../../services/categoryService';
import { useRealtimeSync } from '../../hooks/useRealtimeSync';
import { getImageUrl } from '../../utils/imageUrl';
import '../common/FeaturedProductsSection.css';
import './FeaturedProducts.css';

// Show exactly top 8 best products on home page
const LIMIT = 8;

const FeaturedProducts = () => {
    const [allCategories, setAllCategories] = useState([]);
    const [featuredCategories, setFeaturedCategories] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(true);
    const [activeParentId, setActiveParentId] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [products, setProducts] = useState([]);
    const [loadingInit, setLoadingInit] = useState(true);
    const [error, setError] = useState('');
    const tabsRef = useRef(null);

    const loadCategoryData = useCallback(async ({ forceRefresh = false } = {}) => {
        const [allList, featuredList] = await Promise.all([
            categoryService.getAll({}, { forceRefresh }),
            categoryService
                .getAll({ featured_only: 'true' }, { forceRefresh })
                .catch(() => []),
        ]);
        setAllCategories(Array.isArray(allList) ? allList : []);
        setFeaturedCategories(Array.isArray(featuredList) ? featuredList : []);
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [allList, featuredList] = await Promise.all([
                    categoryService.getAll(),
                    categoryService.getAll({ featured_only: 'true' }).catch(() => []),
                ]);
                if (!cancelled) {
                    setAllCategories(Array.isArray(allList) ? allList : []);
                    setFeaturedCategories(Array.isArray(featuredList) ? featuredList : []);
                }
            } catch (e) {
                console.error('Failed to load featured categories:', e);
                if (!cancelled) {
                    setAllCategories([]);
                    setFeaturedCategories([]);
                }
            } finally {
                if (!cancelled) setCategoriesLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const fetchProducts = useCallback(
        async () => {
            try {
                setLoadingInit(true);
                setError('');

                const { products: newProducts } =
                    await productService.getAll(
                        1,
                        LIMIT,
                        '',
                        selectedCategory,
                        'default',
                        '',
                        true
                    );

                setProducts(Array.isArray(newProducts) ? newProducts.slice(0, LIMIT) : []);
            } catch (err) {
                setError(
                    err.message || 'Failed to load featured products. Try again.'
                );
            } finally {
                setLoadingInit(false);
            }
        },
        [selectedCategory]
    );

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    // Real-time synchronization: automatically refresh on admin product/price/category updates
    useRealtimeSync('products', useCallback(() => {
        fetchProducts();
    }, [fetchProducts]));

    useRealtimeSync('prices', useCallback(() => {
        fetchProducts();
    }, [fetchProducts]));

    useRealtimeSync('categories', useCallback(async () => {
        try {
            await loadCategoryData({ forceRefresh: true });
        } catch (e) {
            console.error('Failed to reload featured categories:', e);
        }
    }, [loadCategoryData]));

    const hierarchy = useMemo(() => buildCategoryHierarchy(allCategories), [allCategories]);

    // Build set of categories (and their ancestor parents) that have featured products
    const featuredRelevantIds = useMemo(() => {
        const set = new Set();
        for (const fc of featuredCategories) {
            const id = String(fc._id || fc.id || '');
            if (!id) continue;
            set.add(id);
            const ancestors = hierarchy.getAncestors(id);
            for (const anc of ancestors) {
                set.add(String(anc._id));
            }
        }
        return set;
    }, [featuredCategories, hierarchy]);

    const visibleParentCategories = useMemo(() => {
        if (featuredCategories.length === 0) return [];
        return hierarchy.parentCategories.filter((p) => featuredRelevantIds.has(String(p._id)));
    }, [featuredCategories.length, hierarchy.parentCategories, featuredRelevantIds]);

    const activeParent = activeParentId ? hierarchy.getCategoryById(activeParentId) : null;

    const visibleSubcategories = useMemo(() => {
        if (!activeParent) return [];
        const allSubs = hierarchy.getSubcategories(activeParent._id);
        const filtered = allSubs.filter((s) => featuredRelevantIds.has(String(s._id)));
        return filtered.length > 0 ? filtered : allSubs;
    }, [activeParent, hierarchy, featuredRelevantIds]);

    const handleTabClick = (cat) => {
        const catId = String(cat._id);
        if (hierarchy.hasSubcategories(catId)) {
            setActiveParentId(catId);
            setSelectedCategory('all');
            if (tabsRef.current) {
                tabsRef.current.scrollTo({ left: 0, behavior: 'smooth' });
            }
        } else {
            setSelectedCategory(catId);
        }
    };

    const handleBackToMainTabs = () => {
        if (activeParent?.parent_id) {
            setActiveParentId(String(activeParent.parent_id));
        } else {
            setActiveParentId(null);
        }
        setSelectedCategory('all');
    };

    const scrollTabs = (dir) => {
        const el = tabsRef.current;
        if (!el) return;
        el.scrollBy({ left: dir === 'left' ? -200 : 200, behavior: 'smooth' });
    };

    const displayedProducts = products.slice(0, 8);

    return (
        <section id="products-section" className="featured-products" aria-label="Featured appliances">
            <div className="container">
                <div className="featured-header-centered">
                    <span className="section-eyebrow">FEATURED APPLIANCES</span>
                    <h2 className="section-title">Popular in our showroom</h2>
                    <Link to="/products" className="featured-view-all">
                        Browse all showroom products →
                    </Link>
                </div>

                <div className="featured-category-wrap">
                    <button
                        type="button"
                        className="featured-tabs-scroll featured-tabs-scroll--left"
                        onClick={() => scrollTabs('left')}
                        aria-label="Scroll categories left"
                    >
                        <FiChevronLeft />
                    </button>
                    <div className="featured-category-bar" ref={tabsRef}>
                        <div className="featured-category-bar-inner">
                            {activeParent ? (
                                <>
                                    <button
                                        type="button"
                                        className="tab-btn tab-btn--back"
                                        onClick={handleBackToMainTabs}
                                    >
                                        <FiArrowLeft aria-hidden="true" />
                                        <span>Main Categories</span>
                                    </button>
                                    <span className="featured-parent-pill-label">
                                        {activeParent.name}:
                                    </span>
                                    {visibleSubcategories.map((sub) => (
                                        <button
                                            key={sub._id}
                                            type="button"
                                            className={`tab-btn ${
                                                selectedCategory === String(sub._id)
                                                    ? 'active'
                                                    : ''
                                            }`}
                                            onClick={() => handleTabClick(sub)}
                                        >
                                            {sub.name}
                                        </button>
                                    ))}
                                </>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        className={`tab-btn ${
                                            selectedCategory === 'all' ? 'active' : ''
                                        }`}
                                        onClick={() => setSelectedCategory('all')}
                                    >
                                        All
                                    </button>
                                    {categoriesLoading ? (
                                        <span className="featured-tabs-loading">
                                            Loading categories…
                                        </span>
                                    ) : (
                                        visibleParentCategories.map((cat) => (
                                            <button
                                                key={cat._id}
                                                type="button"
                                                className={`tab-btn ${
                                                    selectedCategory === String(cat._id)
                                                        ? 'active'
                                                        : ''
                                                }`}
                                                onClick={() => handleTabClick(cat)}
                                            >
                                                {cat.name}
                                            </button>
                                        ))
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                    <button
                        type="button"
                        className="featured-tabs-scroll featured-tabs-scroll--right"
                        onClick={() => scrollTabs('right')}
                        aria-label="Scroll categories right"
                    >
                        <FiChevronRight />
                    </button>
                </div>

                {error && (
                    <div className="featured-products-error">
                        <FiAlertCircle />
                        <span>{error}</span>
                    </div>
                )}

                {loadingInit ? (
                    <div className="products-grid">
                        {[...Array(8)].map((_, i) => (
                            <div key={i} className="product-skeleton" />
                        ))}
                    </div>
                ) : displayedProducts.length === 0 ? (
                    <div className="featured-products-empty">
                        <FiAlertCircle className="empty-icon" />
                        <p>No featured products in this category yet.</p>
                    </div>
                ) : (
                    <>
                        {/* Exactly 8 Best Featured Products */}
                        <div className="products-grid">
                            {displayedProducts.map((product) => (
                                <ProductCard
                                    key={product._id}
                                    product={{
                                        ...product,
                                        image: getImageUrl(product.image),
                                        secondary_image: product.secondary_image
                                            ? getImageUrl(product.secondary_image)
                                            : null,
                                        _imagePath: product.image,
                                        rating: product.rating ?? 0,
                                        reviews: product.reviews ?? 0,
                                        brand: product.brand || '',
                                    }}
                                />
                            ))}
                        </div>

                        {/* Centered Explore All Products Button */}
                        <div className="featured-more-action">
                            <Link to="/products" className="featured-more-btn">
                                Explore All Products &amp; Inventory <FiArrowRight aria-hidden="true" />
                            </Link>
                        </div>
                    </>
                )}
            </div>
        </section>
    );
};

export default FeaturedProducts;
